import { createServerFn } from '@tanstack/react-start'

/**
 * Server functions for accessing environment variables at runtime
 * 
 * These functions allow server-side environment variables to be passed to the client
 * via route loaders, rather than using build-time substitution.
 * 
 * Usage pattern (from TanStack Start docs):
 * ```tsx
 * // In your route file
 * export const Route = createFileRoute('/')({
 *   loader: async () => {
 *     const apiUrl = await getApiBaseUrl()
 *     const stripeKey = await getStripePublishableKey()
 *     return { apiUrl, stripeKey }
 *   },
 *   component: RouteComponent,
 * })
 * 
 * function RouteComponent() {
 *   const { apiUrl, stripeKey } = Route.useLoaderData()
 *   // ... use your variables
 * }
 * ```
 * 
 * For build-time environment variables, use import.meta.env.VITE_* directly in client code.
 */

/**
 * Get the API base URL from server environment
 * Use this when the API URL needs to be determined at runtime (e.g., multi-tenant setups)
 * For static deployments, use import.meta.env.VITE_API_BASE_URL instead
 */
export const getApiBaseUrl = createServerFn({ method: 'GET' })
  .handler(async () => {
    return process.env.VITE_API_BASE_URL
  })

/**
 * Get the Stripe publishable key from server environment
 * Use this when the key needs to be determined at runtime
 * For static deployments, use import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY instead
 */
export const getStripePublishableKey = createServerFn({ method: 'GET' })
  .handler(async () => {
    return process.env.VITE_STRIPE_PUBLISHABLE_KEY
  })

/**
 * Get the Google Client ID from server environment
 * Use this when the client ID needs to be determined at runtime
 * For static deployments, use import.meta.env.VITE_GOOGLE_CLIENT_ID instead
 */
export const getGoogleClientId = createServerFn({ method: 'GET' })
  .handler(async () => {
    return process.env.VITE_GOOGLE_CLIENT_ID
  })

/**
 * Get the Apple Client ID from server environment
 */
export const getAppleClientId = createServerFn({ method: 'GET' })
  .handler(async () => {
    return process.env.VITE_APPLE_CLIENT_ID
  })

/**
 * Get the voice service provider from server environment
 */
export const getVoiceService = createServerFn({ method: 'GET' })
  .handler(async () => {
    return process.env.VITE_VOICE_SERVICE || 'mock'
  })

/**
 * Get the Umami analytics website ID from server environment
 */
export const getUmamiWebsiteId = createServerFn({ method: 'GET' })
  .handler(async () => {
    return process.env.VITE_UMAMI_WEBSITE_ID
  })

/**
 * Get the Sentry DSN from server environment
 */
export const getSentryDsn = createServerFn({ method: 'GET' })
  .handler(async () => {
    return process.env.VITE_SENTRY_DSN
  })

/**
 * Get all root-level env config in a single server round-trip.
 * Use this in the root loader instead of calling individual functions.
 */
export const getEnvConfig = createServerFn({ method: 'GET' })
  .handler(async () => ({
    googleClientId: process.env.VITE_GOOGLE_CLIENT_ID ?? '',
    apiBaseUrl: process.env.VITE_API_BASE_URL ?? '',
    stripePublishableKey: process.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '',
    umamiWebsiteId: process.env.VITE_UMAMI_WEBSITE_ID,
    sentryDsn: process.env.VITE_SENTRY_DSN,
  }))
